import React, { useState, useEffect } from "react";
import { OI_URL } from "../../config/config";
import DataFetchHookOI from "../../helper/DataFetchHookOI";
import PermissionsRequired from "../../helper/PermissionsRequired";
import { Table, PageHeader, Button, Card, Form, Select, Row, Col, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import AssociationsChecker from "../../helper/AssociationsChecker";
import CustomPaginationV2 from "../../helper/Table/CustomPaginationV2";
import SortOrder from "../../helper/Table/SortOrder";
import moment from "moment";
import CustomDateRangeV3 from "../../helper/Table/CustomDateRangeV3";
import axios from "axios";
import Cookies from "js-cookie";

const { Option } = Select;
const DATA_URL = "/api/v1/txshield/rdr/rdrresult/search";
const MERCHANT_DATA_URL = "/api/v1/txshield/merchants/";

const MAX_DATERANGE = 3;
const START_DATE = moment().subtract(MAX_DATERANGE, "months");
const END_DATE = moment();

const RDRDecisionsList = (props) => {
    const { t } = props;
    const [{ data, totalResult, isLoading, requestData }, , setRequestData] = DataFetchHookOI(DATA_URL, "POST", null, {
        page: 1,
        size: 20,
        order: "DESC",
        dateFrom: START_DATE.format("YYYY-MM-DD HH:mm:ss"),
        dateTo: END_DATE.format("YYYY-MM-DD HH:mm:ss"),
    });
    
    const [formData, setFormData] = useState({ hideColumn: [] });
    const [form] = Form.useForm();
    const [currentPage, setCurrentPage] = useState(1);
    const [sortedInfo, setSortedInfo] = useState({});
    const [dates, setDates] = useState([START_DATE, END_DATE]);
    const [isDateExceeded, setIsDateExceeded] = useState(false);
    const [merchantList, setMerchantList] = useState([]);

    const initFormData = {
        merchantId: null,
        bin: null,
        caid: null,
        order: "DESC",
        dateRange: [START_DATE, END_DATE]
    };

    useEffect(() => {
        formValInit();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        getMerchantList();
    }, []);

    const getMerchantList = async () => {
        try {
            const response = await axios.post(
                OI_URL + MERCHANT_DATA_URL,
                { order: 'ASC', sortField: 'name', page: 1, size: 1000 },
                { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } }
            );
            setMerchantList(response.data.datalist || []);
        } catch (error) {
            console.error("Failed to fetch merchants", error);
        }
    };

    const handleChange = (pagination, filters, sorter, { currentDataSource }) => {
        setSortedInfo(sorter);
    };

    const formValInit = () => {
        setFormData(initFormData);
        form.setFieldsValue(initFormData);
    };

    const formValChange = (chgV, allV) => {
        if (chgV.dateRange) {
            setDates(chgV.dateRange);
        }
        setFormData(allV);
    };

    const clearAll = () => {
        const newStartDate = moment().subtract(MAX_DATERANGE, "months");
        const newEndDate = moment();
        setDates([newStartDate, newEndDate]);
        setFormData({
            ...initFormData,
            dateRange: [newStartDate, newEndDate]
        });
        form.setFieldsValue({
            ...initFormData,
            dateRange: [newStartDate, newEndDate]
        });
        setRequestData({ 
            ...requestData, 
            dateFrom: newStartDate.startOf('day').toISOString(),
            dateTo: newEndDate.endOf('day').toISOString(),
            page: 1 
        });
    };

    const onFinish = () => {
        let startDate = null;
        let endDate = null;
        let searchData = {};
        
        if (formData) {
            if (dates) {
                startDate = dates[0] ? dates[0] : null;
                endDate = dates[1] ? dates[1] : null;
            }
            
            searchData = {
                merchantId: formData.merchantId,
                bin: formData.bin ? formData.bin.trim() : null,
                caid: formData.caid ? formData.caid.trim() : null,
                dateFrom: startDate ? moment(startDate).format("YYYY-MM-DD HH:mm:ss") : null,
                dateTo: endDate ? moment(endDate).format("YYYY-MM-DD HH:mm:ss") : null,
            };
        }

        setCurrentPage(1);
        setRequestData({ ...requestData, ...searchData, page: 1 });
    };

    const columns = [
        {
            dataIndex: "dateReceived",
            key: "dateReceived",
            title: "Date Received",
            width: 180,
            sorter: {
                compare: (a, b) => moment(a.dateReceived).diff(moment(b.dateReceived))
            },
            sortOrder: SortOrder(sortedInfo, "dateReceived"),
            render: (dateReceived) => dateReceived ? moment(dateReceived).format("YYYY-MM-DD HH:mm:ss") : ""
        },
        {
            dataIndex: "merchantId",
            key: "merchantId",
            title: "Merchant Name",
            width: 200,
            sorter: (a, b) => {
                const nameA = merchantList.find(m => m.merchantId === a.merchantId)?.name || "";
                const nameB = merchantList.find(m => m.merchantId === b.merchantId)?.name || "";
                return nameA.localeCompare(nameB);
            },
            sortOrder: SortOrder(sortedInfo, "merchantId"),
            render: (_, record) => (
                <span>{merchantList.find(m => m.merchantId === record.merchantId)?.name || ""}</span>
            ),
        },
        {
            dataIndex: "decisionId",
            key: "decisionId",
            title: "Decision ID",
            width: 150,
            sorter: {
                compare: (a, b) => String(a.decisionId || "").localeCompare(String(b.decisionId || ""))
            },
            sortOrder: SortOrder(sortedInfo, "decisionId"),
        },
        {
            dataIndex: "caseType",
            key: "caseType",
            title: "Case Type",
            width: 120,
            sorter: {
                compare: (a, b) => String(a.caseType || "").localeCompare(String(b.caseType || ""))
            },
            sortOrder: SortOrder(sortedInfo, "caseType"),
        },
        {
            dataIndex: "bin",
            key: "bin",
            title: "BIN",
            width: 100,
            sorter: {
                compare: (a, b) => String(a.bin || "").localeCompare(String(b.bin || ""))
            },
            sortOrder: SortOrder(sortedInfo, "bin"),
        },
        {
            dataIndex: "caid",
            key: "caid",
            title: "CAID",
            width: 100,
            sorter: {
                compare: (a, b) => String(a.caid || "").localeCompare(String(b.caid || ""))
            },
            sortOrder: SortOrder(sortedInfo, "caid"),
        },
        {
            dataIndex: "caseDate",
            key: "caseDate",
            title: "Case Date",
            width: 180,
            sorter: {
                compare: (a, b) => moment(a.caseDate).diff(moment(b.caseDate))
            },
            sortOrder: SortOrder(sortedInfo, "caseDate"),
            render: (caseDate) => caseDate ? moment(caseDate).format("YYYY-MM-DD HH:mm:ss") : ""
        },
        {
            dataIndex: "matchedResult",
            key: "matchedResult",
            title: "Result",
            width: 180,
            sortOrder: SortOrder(sortedInfo, "matchedResult"),
            sorter: {
                compare: (a, b) => String(a.matchedResult || "").localeCompare(String(b.matchedResult || ""))
            },
        },
        {
            dataIndex: "matchedRuleName",
            key: "matchedRuleName",
            title: "Matched Rule Name",
            width: 180,
            sortOrder: SortOrder(sortedInfo, "matchedRuleName"),
            sorter: {
                compare: (a, b) => String(a.matchedRuleName || "").localeCompare(String(b.matchedRuleName || ""))
            },
        },
        
    ];

    const getDataSource = (datalist) => {
        return datalist && Array.isArray(datalist) ? datalist : [];
    };

    const getHeaderTable = () => (
        <div className="custom-table--header d-flex justify-content-between align-items-center">
            <h3 className="table-title">RDR Decisions</h3>
        </div>
    );

    const AdvancedSearchForm = () => (
        <div className="search-wrap">
            <Card title="Search" size="small">
                <Form form={form} size="large" name="advanced_search" className="ant-advanced-search-form" onValuesChange={formValChange} onFinish={onFinish}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item 
                                name="dateRange"
                                initialValue={[START_DATE, END_DATE]}
                                rules={[
                                    {
                                        validator: () => {
                                            if (isDateExceeded) {
                                                return Promise.reject('Date range cannot exceed 3 months');
                                            } else if ((dates[0] && !dates[1]) || (!dates[0] && dates[1])) {
                                                return Promise.reject('Please select at least 1 day');
                                            } else {
                                                return Promise.resolve();
                                            }
                                        },
                                    },
                                ]}
                            >
                                <CustomDateRangeV3 
                                    t={t}
                                    setIsDateExceeded={setIsDateExceeded} 
                                    state={{ dates, setDates }} 
                                    unit="months" 
                                    limit={MAX_DATERANGE} 
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="merchantId">
                                <Select 
                                    showSearch 
                                    allowClear
                                    placeholder="Select Merchant"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {merchantList.map(merchant => (
                                        <Option key={merchant.merchantId} value={merchant.merchantId}>
                                            {merchant.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="bin">
                                <Input placeholder="BIN" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="caid">
                                <Input placeholder="CAID" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item>
                                <Button type="secondary" onClick={() => clearAll()} block>
                                    Clear All
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" block>
                                    <SearchOutlined /> Search
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );

    if (!(PermissionsRequired("SYSADMIN") || AssociationsChecker("OI") )) {
        return null;
    }

    return (
        <div>
            <PageHeader title="RDR Decisions"></PageHeader>
            {AdvancedSearchForm()}
            <Table
                loading={isLoading}
                title={() => getHeaderTable()}
                columns={columns}
                rowKey="_id"
                className="table-v3 custom-pagination"
                size="small"
                dataSource={getDataSource(data.datalist)}
                onChange={handleChange}
                pagination={CustomPaginationV2({ 
                    totalResult, 
                    currentPage, 
                    requestData, 
                    setRequestData, 
                    setCurrentPage 
                })}
                locale={{ emptyText: "No records found" }}
            />
        </div>
    );
};

export default RDRDecisionsList;